import React, { useCallback, useEffect, useState } from "react";
import AppLayout from "../../components/AppLayout";
import StatusBadge from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import api, { formatCurrency, formatDate, formatMethod } from "../../lib/api";
import { useTerms } from "../../lib/BrandContext";

export default function History() {
  const { terms } = useTerms();
  // `records === null` = never fetched; `[]` = fetched and genuinely empty.
  // `fetchError` is set only when a fetch failed AND we have no prior data
  // to fall back on. On a background refetch failure we keep the previous
  // records so a transient mobile connectivity hiccup can't wipe the
  // parent's confirmed payment history from view.
  const [records, setRecords] = useState(null);
  const [fetchError, setFetchError] = useState(false);

  const load = useCallback(() => {
    return api.get("/payments")
      .then((r) => {
        setRecords(r.data);
        setFetchError(false);
      })
      .catch(() => {
        // Preserve whatever we already had. Only surface a hard error state
        // when we have literally nothing to show — never overwrite a
        // successful previous response with an empty array.
        setRecords((prev) => (prev == null ? null : prev));
        setFetchError(true);
      });
  }, []);

  useEffect(() => {
    load();
    // Same refetch contract as ParentDashboard + Owed: keep History live
    // when the coach confirms a payment while the parent is still on this
    // screen (PWA/tab visibility flip) or when iOS bfcache restores the
    // page after a swipe-back navigation.
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    const onPageShow = (e) => {
      if (e.persisted) load();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [load]);

  if (records === null) {
    // Hard error before any successful load: show a retry affordance
    // instead of the misleading "No history yet" empty state.
    if (fetchError) {
      return (
        <AppLayout>
          <div className="mt-10 rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center" data-testid="history-error">
            <p className="font-semibold text-rose-800">Couldn't load your history.</p>
            <p className="mt-1 text-sm text-rose-700">Check your connection and try again.</p>
            <Button
              onClick={() => { setFetchError(false); load(); }}
              className="mt-4 bg-brand font-semibold text-white hover:bg-brand-hover"
              data-testid="history-retry"
            >
              Retry
            </Button>
          </div>
        </AppLayout>
      );
    }
    return (
      <AppLayout>
        <div className="flex h-64 items-center justify-center text-ink-500" data-testid="history-loading">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand border-t-transparent" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div>
        <p className="label-caps">Parent</p>
        <h1 className="h2">Payment history</h1>
        <p className="text-sm text-ink-500">Every fee and payment you've been involved with.</p>
      </div>

      {/* Non-blocking notice when a background refetch failed but we still
          have a good previous snapshot. Preserves the parent's ability to
          see prior payments while the connection recovers. */}
      {fetchError ? (
        <div
          className="mt-4 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
          data-testid="history-stale-notice"
        >
          <span>Showing last-known payments — couldn't refresh just now.</span>
          <button
            onClick={() => { setFetchError(false); load(); }}
            className="ml-3 font-semibold text-amber-900 underline"
            data-testid="history-retry-inline"
          >
            Retry
          </button>
        </div>
      ) : null}

      {/* Mobile (< sm): stacked card layout so Due and Status stay
          on-screen in iPhone portrait. No horizontal scroll required.
          `sm:hidden` swaps this out at ≥640px in favour of the desktop
          table below, so tablets and laptops keep the scannable
          columnar view. */}
      <div className="mt-6 space-y-3 sm:hidden">
        {records.length === 0 ? (
          <div className="rounded-xl border border-ink-100 bg-white p-6 text-center text-ink-500 shadow-sm" data-testid="history-empty-mobile">
            No history yet.
          </div>
        ) : records.map((r) => (
          <article
            key={r.id}
            className="rounded-xl border border-ink-100 bg-white p-4 shadow-sm"
            data-testid={`history-row-${r.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="truncate font-heading text-base font-bold text-ink-900">
                  {r.charge?.title}
                </h3>
                <p className="mt-0.5 truncate text-xs text-ink-500">
                  {r.player?.first_name} {r.player?.last_name}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="font-heading text-lg font-bold text-ink-900">{formatCurrency(r.amount)}</p>
                <div className="mt-1"><StatusBadge status={r.status} /></div>
              </div>
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 border-t border-ink-100 pt-3 text-xs">
              <div>
                <dt className="text-ink-500">Method</dt>
                <dd className="font-medium text-ink-900">{formatMethod(r.method) || "—"}</dd>
              </div>
              <div className="text-right">
                <dt className="text-ink-500">Due</dt>
                <dd className="font-medium text-ink-900">{formatDate(r.charge?.due_date)}</dd>
              </div>
            </dl>
          </article>
        ))}
      </div>

      {/* Desktop / tablet (≥ sm): keep the original scannable table. */}
      <div className="mt-6 hidden overflow-hidden rounded-xl border border-ink-100 bg-white shadow-sm sm:block">
        <table className="w-full text-sm">
          <thead className="bg-ink-100 text-left text-xs font-bold uppercase tracking-wide text-ink-500">
            <tr>
              <th className="px-4 py-3">Fee</th>
              <th className="px-4 py-3">{terms.Athlete}</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Method</th>
              <th className="px-4 py-3">Due</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {records.map((r) => (
              <tr key={r.id} className="text-ink-700" data-testid={`history-row-desktop-${r.id}`}>
                <td className="px-4 py-3 font-semibold text-ink-900">{r.charge?.title}</td>
                <td className="px-4 py-3">{r.player?.first_name} {r.player?.last_name}</td>
                <td className="px-4 py-3">{formatCurrency(r.amount)}</td>
                <td className="px-4 py-3">{formatMethod(r.method) || "—"}</td>
                <td className="px-4 py-3">{formatDate(r.charge?.due_date)}</td>
                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
              </tr>
            ))}
            {records.length === 0 ? (
              <tr><td colSpan={6} className="p-6 text-center text-ink-500" data-testid="history-empty">No history yet.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}
